import { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';

// Create the Error Context
const ErrorContext = createContext(undefined);

// Export ErrorContext for use in class components (e.g., ErrorBoundary)
export { ErrorContext };

// Configuration constants
const AUTO_DISMISS_DELAY = 10000; // 10 seconds for non-critical errors
const MAX_ERROR_HISTORY = 10; // Keep last 10 errors for debugging

/**
 * ErrorProvider component that manages global error state
 * Provides error context to all child components
 */
export const ErrorProvider = ({ children }) => {
  // Current error state
  const [error, setError] = useState(null);
  
  // Error history for debugging (last 10 errors)
  const [errorHistory, setErrorHistory] = useState([]);
  
  // Auto-dismiss timer reference
  const autoDismissTimer = useRef(null);
  
  // Retry callback reference
  const retryCallback = useRef(null);

  /**
   * Clear any existing auto-dismiss timer
   */
  const clearAutoDismissTimer = useCallback(() => {
    if (autoDismissTimer.current) {
      clearTimeout(autoDismissTimer.current);
      autoDismissTimer.current = null;
    }
  }, []);

  /**
   * Clear the current error state
   */
  const clearError = useCallback(() => {
    clearAutoDismissTimer();
    setError(null);
    retryCallback.current = null;
  }, [clearAutoDismissTimer]);

  /**
   * Show an error with the specified details
   * @param {string} type - Error type: 'network' | 'server' | 'client' | 'auth'
   * @param {string} message - User-friendly error message
   * @param {object} details - Additional error details (statusCode, endpoint, method, originalError)
   * @param {boolean} retryable - Whether the error can be retried
   * @param {function} onRetry - Optional callback function to retry the failed operation
   */
  const showError = useCallback((type, message, details = {}, retryable = false, onRetry = null) => {
    // Clear any existing timer
    clearAutoDismissTimer();

    const newError = {
      type,
      message,
      details: {
        ...details,
        timestamp: Date.now()
      },
      retryable
    };

    // Update current error
    setError(newError);

    // Store retry callback if provided
    if (onRetry && typeof onRetry === 'function') {
      retryCallback.current = onRetry;
    } else {
      retryCallback.current = null;
    }

    // Add to error history (keep last 10)
    setErrorHistory(prevHistory => {
      const updatedHistory = [...prevHistory, newError];
      return updatedHistory.slice(-MAX_ERROR_HISTORY);
    });

    // Set auto-dismiss timer for non-critical errors (client errors only)
    if (type === 'client') {
      autoDismissTimer.current = setTimeout(() => {
        clearError();
      }, AUTO_DISMISS_DELAY);
    }

    // Log error to console for debugging
    console.error('Global Error Handler:', {
      type,
      message,
      details,
      retryable,
      timestamp: new Date().toISOString()
    });
  }, [clearAutoDismissTimer, clearError]);

  /**
   * Retry the last failed request if a retry callback is available
   * @returns {Promise<void>}
   */
  const retryLastRequest = useCallback(async () => {
    if (retryCallback.current && typeof retryCallback.current === 'function') {
      try {
        // Clear current error before retrying
        clearError();
        
        // Execute retry callback
        await retryCallback.current();
        
        console.log('Retry successful');
      } catch (error) {
        console.error('Retry failed:', error);
        
        // Show error again if retry fails
        showError(
          'network',
          'Retry failed. Please try again.',
          {
            originalError: error,
            retryAttempt: true
          },
          true,
          retryCallback.current
        );
      }
    } else {
      console.warn('No retry callback available');
    }
  }, [clearError, showError]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    error,
    errorHistory,
    showError,
    clearError,
    retryLastRequest
  }), [error, errorHistory, showError, clearError, retryLastRequest]);

  return (
    <ErrorContext.Provider value={contextValue}>
      {children}
    </ErrorContext.Provider>
  );
};

/**
 * Custom hook to consume error context
 * @returns {Object} Error context value with error, showError, clearError, and retryLastRequest
 * @throws {Error} If used outside of ErrorProvider
 */
export const useError = () => {
  const context = useContext(ErrorContext);

  if (context === undefined) {
    console.error('useError hook was called outside of ErrorProvider. Ensure the component is wrapped with ErrorProvider.');
    throw new Error('useError must be used within an ErrorProvider');
  }

  return context;
};
